import { useState, useEffect, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  Download, FileText, CheckCircle2, Loader2, ArrowRight,
  Users2, CalendarDays, ArrowRightLeft, ClipboardList, Building2, Timer,
} from 'lucide-react';
import { PageHeader } from '@/components/shared/PageHeader';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/cn';
import { generateReport } from '@/lib/ReportGenerator';
import { useDailyAttendance, useMonthlyAllAttendance } from '@/api/queries/attendance.queries';
import { useEmployees } from '@/api/queries/employee.queries';
import { selectCompanyName } from '@/store/slices/companySlice';

const REPORT_TYPES = [
  { id: 'attendance_summary',   name: 'Attendance Summary',           description: 'Department-wise daily/monthly attendance', category: 'Attendance' },
  { id: 'attendance_detail',    name: 'Detailed Attendance Register', description: 'Employee-level punch-in/out records',       category: 'Attendance' },
  { id: 'late_arrivals',        name: 'Late Arrivals Report',         description: 'Employees with late check-ins',             category: 'Attendance' },
  { id: 'payroll_summary',      name: 'Payroll Summary',              description: 'Gross, deductions, net pay per employee',  category: 'Payroll'    },
  { id: 'salary_register',      name: 'Salary Register',             description: 'Full month salary register with PF/ESIC',  category: 'Payroll'    },
  { id: 'statutory_deductions', name: 'Statutory Deductions',         description: 'PF, ESIC, PT, TDS breakdowns',             category: 'Payroll'    },
  { id: 'leave_balance',        name: 'Leave Balance Report',         description: 'Current leave balances for all employees', category: 'Leave'      },
  { id: 'leave_history',        name: 'Leave History',               description: 'Leave applications with status',            category: 'Leave'      },
  { id: 'headcount',            name: 'Headcount Report',             description: 'Employee strength by department/type',     category: 'Employees'  },
  { id: 'new_joiners',          name: 'New Joiners Report',           description: 'Employees joined in period',               category: 'Employees'  },
  { id: 'expense_summary',      name: 'Expense Claims Report',        description: 'Claims by employee/category',             category: 'Expenses'   },
];

// Punch / Attendance analytics reports — navigate to their own dedicated page
const ANALYTICS_REPORTS = [
  { id: 'active_employees', name: 'Active Employee Report',    description: 'All active employees with attendance %, leave taken, and employment type breakdown', icon: Users2,         href: '/reports/active-employees', color: 'blue'   },
  { id: 'monthly_grid',     name: 'Monthly Attendance Report', description: 'Calendar grid showing P/A/L/S per employee per day with OT tracking',               icon: CalendarDays,   href: '/reports/monthly',          color: 'green'  },
  { id: 'swipe_report',     name: 'Swipe Report',              description: 'All biometric punch logs with anomaly detection and grouped view',                    icon: ArrowRightLeft, href: '/reports/swipe',            color: 'purple' },
  { id: 'attendance_deep',  name: 'Attendance Deep Report',    description: 'Detailed day-level attendance with filters by status, department, and method',        icon: ClipboardList,  href: '/reports/attendance',       color: 'yellow' },
  { id: 'strength',         name: 'Strength Report',           description: 'Department-wise present/absent/leave breakdown with donut charts',                    icon: Building2,      href: '/reports/strength',         color: 'orange' },
  { id: 'ot_report',        name: 'OT Report',                 description: 'Overtime tracking with per-employee daily breakdown and estimated OT pay',            icon: Timer,          href: '/reports/ot',               color: 'red'    },
];

const ANALYTICS_COLOR = {
  blue:   { bg: 'bg-blue-50 dark:bg-blue-900/20',   icon: 'text-blue-600'   },
  green:  { bg: 'bg-green-50 dark:bg-green-900/20', icon: 'text-green-600'  },
  purple: { bg: 'bg-purple-50 dark:bg-purple-900/20', icon: 'text-purple-600' },
  yellow: { bg: 'bg-yellow-50 dark:bg-yellow-900/20', icon: 'text-yellow-600' },
  orange: { bg: 'bg-orange-50 dark:bg-orange-900/20', icon: 'text-orange-600' },
  red:    { bg: 'bg-red-50 dark:bg-red-900/20',     icon: 'text-red-600'    },
};

// Only attendance report types support client-side live generation
const ATTENDANCE_REPORTS = new Set(['attendance_summary', 'attendance_detail', 'late_arrivals']);

const CATEGORIES = ['All', ...new Set(REPORT_TYPES.map(r => r.category))];

const inputCls = 'w-full text-[13px] border border-[var(--border)] rounded-[8px] px-3 py-2 bg-[var(--bg-muted)] text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)]/40';

export default function ReportsPage() {
  const [searchParams] = useSearchParams();
  const navigate       = useNavigate();
  const companyName    = useSelector(selectCompanyName);

  const [cat, setCat] = useState('All');
  const [selected, setSelected] = useState(() => searchParams.get('type') || null);
  const [dateFrom, setDateFrom] = useState(() => searchParams.get('from') || '2026-06-01');
  const [dateTo, setDateTo]     = useState(() => searchParams.get('to')   || '2026-06-30');
  const [fmt, setFmt]           = useState('xlsx');
  const [generating, setGenerating]   = useState(false);
  const [lastGenerated, setLastGenerated] = useState(null);

  useEffect(() => {
    const type = searchParams.get('type');
    if (type) {
      const match = REPORT_TYPES.find(r => r.id === type);
      if (match) setCat(match.category);
    }
  }, []);

  const isAttendance = ATTENDANCE_REPORTS.has(selected);
  const month = dateFrom.slice(0, 7); // 'YYYY-MM'
  const { data: empResp } = useEmployees();
  const employees = empResp?.data || [];
  const { data: monthlyAllData } = useMonthlyAllAttendance(month, {}, null, employees);
  // Also keep daily for "today only" edge case
  const { data: dailyResp } = useDailyAttendance({ date: dateFrom }, { enabled: false });

  const attendanceRecords = useMemo(() => {
    if (!isAttendance) return [];
    const rows = (monthlyAllData?.data || []).flatMap(emp =>
      Object.entries(emp.days || {}).map(([day, code]) => ({
        employee_code: emp.employee_code,
        name: emp.name,
        department: typeof emp.department === 'object' ? emp.department?.name : emp.department,
        designation: typeof emp.designation === 'object' ? emp.designation?.name : emp.designation,
        date: `${month}-${String(day).padStart(2, '0')}`,
        status: code === 'P' ? 'PRESENT' : code === 'A' ? 'ABSENT' : code === 'L' ? 'ON_LEAVE' : code === 'H' ? 'HOLIDAY' : code === 'W' ? 'WEEKLY_OFF' : String(code),
        check_in:  emp.times?.[day]?.in  || null,
        check_out: emp.times?.[day]?.out || null,
      }))
    ).filter(r => r.date >= dateFrom && r.date <= dateTo);
    return rows.length > 0 ? rows : (dailyResp?.data || []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [monthlyAllData, isAttendance, dateFrom, dateTo, month, dailyResp]);

  const handleGenerate = () => {
    if (!selected) return;
    setGenerating(true);
    const filterSummary = `${dateFrom} to ${dateTo}`;
    try {
      if (isAttendance) {
        generateReport({ type: selected, dateFrom, dateTo, records: attendanceRecords, format: fmt, companyName: companyName || 'AttendX', filterSummary });
        setLastGenerated({ type: selected, fmt, time: new Date() });
      } else {
        const rType = REPORT_TYPES.find(r => r.id === selected);
        generateReport({
          type: 'attendance_detail', dateFrom, dateTo,
          records: empResp?.data?.map(e => ({
            employee_code: e.employee_code,
            name: `${e.first_name} ${e.last_name}`.trim(),
            department: e.department, designation: e.designation,
            date: dateFrom, status: 'PRESENT',
          })) || [],
          format: fmt, companyName: companyName || 'AttendX',
          filterSummary: `${rType?.name} — ${filterSummary} (sample data)`,
        });
        setLastGenerated({ type: selected, fmt, time: new Date() });
      }
    } catch (err) {
      console.error('Report generation failed:', err);
    } finally {
      setGenerating(false);
    }
  };

  const reports = cat === 'All' ? REPORT_TYPES : REPORT_TYPES.filter(r => r.category === cat);

  return (
    <div className="space-y-6 fade-in">
      <PageHeader title="Reports" subtitle="Generate and export reports"/>

      {/* ── Punch & Attendance Analytics ───────────────────────────────── */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <h2 className="text-[13px] font-bold text-[var(--text-primary)] uppercase tracking-wide">Punch &amp; Attendance Analytics</h2>
          <div className="h-px flex-1 bg-[var(--border)]"/>
          <span className="text-[11px] text-[var(--text-tertiary)]">Live data · dedicated pages</span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {ANALYTICS_REPORTS.map(r => {
            const colors = ANALYTICS_COLOR[r.color];
            return (
              <button key={r.id} onClick={() => navigate(r.href)}
                className="card text-left border-2 border-transparent p-5 hover:shadow-md hover:border-[var(--accent)]/30 transition-all group">
                <div className="flex items-start gap-3">
                  <div className={cn('w-9 h-9 rounded-[8px] flex items-center justify-center shrink-0', colors.bg)}>
                    <r.icon size={16} className={colors.icon}/>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <p className="text-[13px] font-semibold text-[var(--text-primary)]">{r.name}</p>
                      <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-[var(--accent-muted)] text-[var(--accent)]">LIVE</span>
                    </div>
                    <p className="text-[12px] text-[var(--text-secondary)] mt-0.5 leading-relaxed">{r.description}</p>
                  </div>
                </div>
                <div className="mt-3 flex items-center gap-1 text-[11px] font-medium text-[var(--accent)] opacity-0 group-hover:opacity-100 transition-opacity">
                  Open report <ArrowRight size={11}/>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* ── Standard Report Generator ──────────────────────────────────── */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <h2 className="text-[13px] font-bold text-[var(--text-primary)] uppercase tracking-wide">Standard Reports</h2>
          <div className="h-px flex-1 bg-[var(--border)]"/>
        </div>

        {/* Category tabs */}
        <div className="flex gap-2 overflow-x-auto pb-1 flex-wrap mb-3">
          {CATEGORIES.map(c => (
            <button key={c} onClick={() => setCat(c)}
              className={cn('px-4 py-1.5 rounded-full text-[12px] font-semibold whitespace-nowrap transition-colors',
                cat === c ? 'bg-[var(--accent)] text-white' : 'bg-[var(--bg-muted)] text-[var(--text-secondary)] hover:bg-[var(--bg-elevated)]'
              )}>{c}</button>
          ))}
        </div>

        {/* Report cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {reports.map(r => (
            <button key={r.id} onClick={() => setSelected(r.id === selected ? null : r.id)}
              className={cn('card text-left border-2 transition-all p-5 hover:shadow-md',
                selected === r.id ? 'border-[var(--accent)]' : 'border-transparent'
              )}>
              <div className="flex items-start gap-3">
                <div className={cn('w-9 h-9 rounded-[8px] flex items-center justify-center shrink-0',
                  ATTENDANCE_REPORTS.has(r.id) ? 'bg-green-50 dark:bg-green-900/20' : 'bg-[var(--accent-muted)]'
                )}>
                  <FileText size={16} className={ATTENDANCE_REPORTS.has(r.id) ? 'text-green-600' : 'text-[var(--accent)]'}/>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <p className="text-[13px] font-semibold text-[var(--text-primary)]">{r.name}</p>
                    {ATTENDANCE_REPORTS.has(r.id) && (
                      <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400">LIVE</span>
                    )}
                  </div>
                  <p className="text-[12px] text-[var(--text-secondary)] mt-0.5">{r.description}</p>
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Generate panel */}
      {selected && (
        <div className="card space-y-4 p-5">
          <div className="flex items-center justify-between">
            <h3 className="text-[14px] font-semibold text-[var(--text-primary)]">
              Generate: {REPORT_TYPES.find(r => r.id === selected)?.name}
            </h3>
            {ATTENDANCE_REPORTS.has(selected) && (
              <span className="text-[11px] font-semibold text-green-600 flex items-center gap-1">
                <CheckCircle2 size={12}/> Client-side — instant download
              </span>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
            <div>
              <label className="text-[12px] font-semibold text-[var(--text-secondary)] block mb-1">From</label>
              <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className={inputCls}/>
            </div>
            <div>
              <label className="text-[12px] font-semibold text-[var(--text-secondary)] block mb-1">To</label>
              <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className={inputCls}/>
            </div>
            <div>
              <label className="text-[12px] font-semibold text-[var(--text-secondary)] block mb-1">Format</label>
              <select value={fmt} onChange={e => setFmt(e.target.value)} className={inputCls}>
                <option value="xlsx">Excel (.xlsx)</option>
                <option value="csv">CSV</option>
                <option value="pdf">PDF (Print)</option>
                <option value="web">Web Page</option>
              </select>
            </div>
            <div className="flex items-end">
              <Button icon={generating ? Loader2 : Download} onClick={handleGenerate} disabled={generating} className="w-full">
                {generating ? 'Generating…' : 'Generate'}
              </Button>
            </div>
          </div>

          {lastGenerated && (
            <div className="flex items-center gap-2 text-[12px] text-green-600 dark:text-green-400">
              <CheckCircle2 size={14}/>
              Last generated: {REPORT_TYPES.find(r => r.id === lastGenerated.type)?.name} as {lastGenerated.fmt.toUpperCase()} at {lastGenerated.time.toLocaleTimeString('en-IN')}
            </div>
          )}

          {!ATTENDANCE_REPORTS.has(selected) && (
            <p className="text-[11px] text-[var(--text-tertiary)]">
              Non-attendance reports (Payroll, Leave, Expenses) will include full live data once backend services are connected. Currently generates a sample export.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
